import { ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Settings,
  FileText,
  Eye,
  FileBarChart,
  BarChart3,
  Home,
  FolderKanban,
  Clock,
  Map,
  PenTool,
  CalendarClock,
  Wrench,
  ScrollText,
  AlertTriangle,
  Hammer,
  Package,
  ArrowLeftRight,
  KeyRound,
  Hash,
  Receipt,
  FileSignature,
  Layers,
  Ruler,
  ClipboardList,
  BookOpen,
  Search,
  Building2,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import BenutzerMenue from './BenutzerMenue';
import CommandPalette from './CommandPalette';

interface LayoutProps {
  children: ReactNode;
}

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  matchPaths?: string[]; // Additional paths to highlight this nav item
}

/**
 * Menuegruppe. `titel: null` steht ohne Kopfzeile oben und ist immer
 * sichtbar (Dashboard); alle anderen lassen sich ein- und ausklappen.
 *
 * Die Gruppen folgen dem Arbeitsablauf: erfassen und pruefen, daraus
 * Auftraege und Dokumente, wiederkehrende Wartung, Abrechnung, und zuletzt
 * die Stammdaten, auf die alles zurueckgreift.
 *
 * Standardmaessig sind alle Gruppen OFFEN: wer das Menue nicht kennt, soll
 * nichts suchen muessen, und die E2E-Pruefung (e2e/navigation.spec.ts)
 * erreicht jeden Punkt ohne Klick auf eine Gruppe. Die Gruppe der aktuellen
 * Seite oeffnet sich in jedem Fall selbst.
 */
interface NavGruppe {
  titel: string | null;
  eintraege: NavItem[];
}

const navGruppen: NavGruppe[] = [
  {
    titel: null,
    eintraege: [{ path: '/', label: 'Dashboard', icon: <Home size={20} /> }],
  },
  {
    titel: 'Erfassung & Prüfung',
    eintraege: [
      { path: '/erfassung', label: 'Erfassung', icon: <FileText size={20} /> },
      { path: '/uebersicht', label: 'Übersicht', icon: <Eye size={20} /> },
      // Direkt hinter der Uebersicht: wer eine Liste von Bauteilen
      // durchsieht, geht als naechstes zu dem, was daran zu beanstanden ist.
      { path: '/maengel', label: 'Mängel', icon: <AlertTriangle size={20} /> },
      { path: '/planverortung', label: 'Planverortung', icon: <Map size={20} /> },
      { path: '/grundriss-editor', label: 'Grundriss-Editor', icon: <PenTool size={20} /> },
    ],
  },
  {
    titel: 'Aufträge & Berichte',
    eintraege: [
      { path: '/projekte', label: 'Projekte', icon: <FolderKanban size={20} /> },
      // Arbeitsauftraege stehen hinter den Projekten und Maengeln: aus
      // einem Mangel entsteht typischerweise die Beauftragung seiner Behebung.
      { path: '/arbeitsauftraege', label: 'Arbeitsaufträge', icon: <Hammer size={20} />, matchPaths: ['/arbeitsauftraege'] },
      { path: '/berichte', label: 'Berichte', icon: <FileBarChart size={20} /> },
      // Gutachten stehen bei den Berichten: es ist ein Dokument, das das
      // Haus verlaesst.
      { path: '/gutachten', label: 'Gutachten', icon: <ScrollText size={20} />, matchPaths: ['/gutachten'] },
      { path: '/auswertung', label: 'Auswertung', icon: <BarChart3 size={20} /> },
    ],
  },
  {
    titel: 'Wartung & Zeit',
    eintraege: [
      { path: '/faelligkeiten', label: 'Fälligkeiten', icon: <CalendarClock size={20} /> },
      { path: '/wartungen', label: 'Wartungen', icon: <Wrench size={20} /> },
      // Direkt hinter den Wartungen: dort steht die Frage "warum entsteht
      // keine neue Wartung", und hier wird sie beantwortet.
      { path: '/wartungsvorlagen', label: 'Wartungsvorlagen', icon: <ClipboardList size={20} />, matchPaths: ['/wartungsvorlagen'] },
      { path: '/zeiterfassung', label: 'Zeiterfassung', icon: <Clock size={20} /> },
    ],
  },
  {
    titel: 'Abrechnung',
    eintraege: [
      // Angebote und Rechnungen stehen beieinander: sie sind das, was aus
      // der geleisteten Arbeit an den Kunden geht.
      { path: '/angebote', label: 'Angebote', icon: <FileSignature size={20} />, matchPaths: ['/angebote'] },
      { path: '/rechnungen', label: 'Rechnungen', icon: <Receipt size={20} />, matchPaths: ['/rechnungen'] },
      // Direkt hinter den Rechnungen: dort steht die Frage "warum fehlt die
      // 00042", und dort wird sie beantwortet.
      { path: '/nummernkreise', label: 'Nummernkreise', icon: <Hash size={20} />, matchPaths: ['/nummernkreise'] },
    ],
  },
  {
    titel: 'Stammdaten & Wissen',
    eintraege: [
      { path: '/produktkatalog', label: 'Produktkatalog', icon: <Package size={20} /> },
      // Pruefbereiche, Grenzwerte und Wissensdatenbank beantworten dieselbe
      // Frage von verschiedenen Seiten: wonach wird geprueft, ab wann ist
      // ein Mass zu gross, und worauf ist dabei zu achten.
      { path: '/pruefbereiche', label: 'Prüfbereiche', icon: <Layers size={20} />, matchPaths: ['/pruefbereiche'] },
      { path: '/mass-grenzwerte', label: 'Maßgrenzwerte', icon: <Ruler size={20} />, matchPaths: ['/mass-grenzwerte'] },
      { path: '/wissen', label: 'Wissensdatenbank', icon: <BookOpen size={20} />, matchPaths: ['/wissen'] },
      // Freischaltungen sind taegliche Arbeit -- wer einen Monteur zu einem
      // Einsatz schickt, schaltet ihn dabei frei --, deshalb hier und nicht
      // bei den Einstellungen.
      { path: '/freischaltungen', label: 'Freischaltungen', icon: <KeyRound size={20} />, matchPaths: ['/freischaltungen'] },
      { path: '/import-export', label: 'Import / Export', icon: <ArrowLeftRight size={20} /> },
    ],
  },
  {
    titel: 'Verwaltung',
    eintraege: [
      { path: '/einstellungen', label: 'Einstellungen', icon: <Settings size={20} />, matchPaths: ['/einstellungen'] },
    ],
  },
];

/** Welche Gruppen der Benutzer eingeklappt hat; pro Browser gemerkt. */
const SPEICHER_SCHLUESSEL = 'menue-gruppen-zu';

function geschlosseneLaden(): Record<string, boolean> {
  try {
    const roh = localStorage.getItem(SPEICHER_SCHLUESSEL);
    return roh ? (JSON.parse(roh) as Record<string, boolean>) : {};
  } catch {
    // Speicher gesperrt oder Inhalt kaputt: alle Gruppen offen.
    return {};
  }
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const [sucheOffen, setSucheOffen] = useState(false);

  // Nur fuer den Betreiber der Plattform: Mandantenliste und Freischaltung.
  const { benutzer: angemeldet } = useAuth();
  const gruppen: NavGruppe[] = angemeldet?.ist_plattform_admin
    ? navGruppen.map((g) =>
        g.titel === 'Verwaltung'
          ? {
              ...g,
              eintraege: [
                ...g.eintraege,
                { path: '/plattform', label: 'Plattform', icon: <Building2 size={20} /> },
              ],
            }
          : g
      )
    : navGruppen;

  const isPathActive = (item: NavItem) => {
    if (location.pathname === item.path) return true;
    if (item.matchPaths) {
      return item.matchPaths.some(p => location.pathname.startsWith(p));
    }
    return false;
  };

  const [zu, setZu] = useState<Record<string, boolean>>(geschlosseneLaden);

  const gruppeUmschalten = (titel: string) => {
    setZu((alt) => {
      const neu = { ...alt, [titel]: !alt[titel] };
      try {
        localStorage.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(neu));
      } catch {
        // nicht gemerkt, aber bedienbar
      }
      return neu;
    });
  };

  // Wer per Link, Suche oder Adresse auf einer Seite landet, soll das Menue
  // dazu sehen: die Gruppe dieser Seite oeffnet sich. Nur beim
  // Seitenwechsel -- ein bewusstes Einklappen auf der aktuellen Seite
  // bleibt stehen.
  useEffect(() => {
    const aktiv = gruppen.find((g) => g.titel && g.eintraege.some(isPathActive));
    if (aktiv?.titel && zu[aktiv.titel]) {
      setZu((alt) => ({ ...alt, [aktiv.titel as string]: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Plattformweite Suche (PLAN_V4_SAAS_ROADMAP Phase 10, Punkt 6):
  // Strg+K / Cmd+K oeffnet von ueberall in der Oberflaeche die
  // Command-Palette. Am Layout und nicht an einer einzelnen Seite, damit
  // der Shortcut wirklich ueberall funktioniert und nicht nur dort, wo
  // jemand daran gedacht hat.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSucheOffen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="flex h-screen bg-secondary-50">
      <CommandPalette open={sucheOffen} onClose={() => setSucheOffen(false)} />

      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-secondary-200 flex flex-col">
        {/* Logo/Header */}
        <div className="p-4 border-b border-secondary-200">
          <h1 className="text-lg font-bold text-primary-600">
            BRANTAS
          </h1>
          <p className="text-xs text-secondary-600">Software · Brandschutz Dokumentation</p>
        </div>

        {/* Plattformweite Suche */}
        <div className="p-3 border-b border-secondary-200">
          <button
            onClick={() => setSucheOffen(true)}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-secondary-500 bg-secondary-50 hover:bg-secondary-100 transition-colors"
          >
            <Search size={16} />
            <span className="flex-1 text-left">Suchen…</span>
            <kbd className="text-[10px] font-mono text-secondary-400 border border-secondary-300 rounded px-1 py-0.5">
              Strg+K
            </kbd>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto" aria-label="Hauptmenü">
          {gruppen.map((gruppe) => {
            const eintraegeAnzeigen = gruppe.eintraege.map((item) => {
              const isActive = isPathActive(item);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors text-sm
                    ${isActive
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-secondary-700 hover:bg-secondary-100'
                    }
                  `}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              );
            });

            if (!gruppe.titel) {
              return (
                <div key="ohne-titel" className="space-y-0.5">
                  {eintraegeAnzeigen}
                </div>
              );
            }

            const titel = gruppe.titel;
            const geschlossen = !!zu[titel];
            const enthaeltAktive = gruppe.eintraege.some(isPathActive);
            const bereichId = `menue-gruppe-${titel.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;

            return (
              <div key={titel} className="pt-1">
                <button
                  type="button"
                  onClick={() => gruppeUmschalten(titel)}
                  aria-expanded={!geschlossen}
                  aria-controls={bereichId}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-xs font-semibold uppercase tracking-wide transition-colors hover:bg-secondary-100 ${
                    enthaeltAktive ? 'text-primary-700' : 'text-secondary-500'
                  }`}
                >
                  <span>{titel}</span>
                  {geschlossen ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </button>
                {!geschlossen && (
                  <div id={bereichId} className="mt-0.5 space-y-0.5">
                    {eintraegeAnzeigen}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-2 border-t border-secondary-200">
          <BenutzerMenue />
          <p className="mt-1 text-[10px] text-secondary-500 text-center">
            Version {__APP_VERSION__}
          </p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
